import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { API_BASE_URL } from '../services/api';

const Prospecting = () => {
  const navigate = useNavigate();
  
  // Filter states
  const [filters, setFilters] = useState({
    // Cost filters
    min_admin_cost_per_participant: '',
    max_admin_cost_per_participant: '',
    min_admin_cost_pct_of_assets: '',
    max_admin_cost_pct_of_assets: '',
    min_admin_expense_bps: '',
    max_admin_expense_bps: '',
    min_admin_cost_pct_of_contributions: '',
    max_admin_cost_pct_of_contributions: '',
    min_total_admin_expenses: '',
    max_total_admin_expenses: '',
    
    // Size filters
    min_participants: '',
    max_participants: '',
    min_assets: '',
    max_assets: '',
    size_category: '',
    
    // Quality filters
    min_participation_rate: '',
    max_participation_rate: '',
    
    // Location
    states: [],
    
    // Industry
    industry_codes: [],
    
    // Plan type exclusions (NEW)
    exclude_plan_categories: [],
    exclude_plan_subtypes: [],
    
    // Other
    min_plan_age_years: '',
    exclude_terminated: true,
    
    // Sorting
    sort_by: 'opportunity_score',
    sort_order: 'desc',
    
    // Pagination
    limit: 50,
    offset: 0
  });
  
  const [results, setResults] = useState([]);
  const [totalResults, setTotalResults] = useState(0);
  const [summaryStats, setSummaryStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statesList, setStatesList] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [planCategories, setPlanCategories] = useState([]);
  const [planSubtypes, setPlanSubtypes] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  
  const sizeCategories = [
    { value: '', label: 'All Sizes' },
    { value: 'Micro', label: 'Micro (1-9)' },
    { value: 'Small', label: 'Small (10-49)' },
    { value: 'Medium-Small', label: 'Medium-Small (50-249)' },
    { value: 'Medium', label: 'Medium (250-999)' },
    { value: 'Large', label: 'Large (1000+)' }
  ];
  
  const sortOptions = [
    { value: 'opportunity_score', label: 'Opportunity Score' },
    { value: 'admin_cost_per_participant', label: 'Admin Cost/Participant' },
    { value: 'admin_cost_pct_of_assets', label: 'Admin Cost % of Assets' },
    { value: 'estimated_savings', label: 'Estimated Savings' },
    { value: 'assets', label: 'Plan Assets' },
    { value: 'participants', label: 'Participants' }
  ];

  // Load states and industries on mount
  useEffect(() => {
    loadStates();
    loadIndustries();
    loadPlanTypes();
  }, []);

  const loadStates = async () => {
    try {
      const data = await api.getProspectingStates();
      setStatesList(data);
    } catch (err) {
      console.error('Error loading states:', err);
    }
  };

  const loadIndustries = async () => {
    try {
      const data = await api.getResearchIndustries();
      setIndustries(data);
    } catch (err) {
      console.error('Error loading industries:', err);
    }
  };

  const loadPlanTypes = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/prospecting/plan-types`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setPlanCategories(data.categories || []);
      setPlanSubtypes(data.subtypes || []);
    } catch (err) {
      console.error('Error loading plan types:', err);
      // Set empty arrays as fallback to prevent undefined errors
      setPlanCategories([]);
      setPlanSubtypes([]);
    }
  };

  const handleSearch = async (page = 1) => {
    setLoading(true);
    setError(null);
    setHasSearched(true);
    setCurrentPage(page);
    
    try {
      // Build filter object, converting empty strings to null
      const searchFilters = {
        min_admin_cost_per_participant: filters.min_admin_cost_per_participant ? parseFloat(filters.min_admin_cost_per_participant) : null,
        max_admin_cost_per_participant: filters.max_admin_cost_per_participant ? parseFloat(filters.max_admin_cost_per_participant) : null,
        min_admin_cost_pct_of_assets: filters.min_admin_cost_pct_of_assets ? parseFloat(filters.min_admin_cost_pct_of_assets) : null,
        max_admin_cost_pct_of_assets: filters.max_admin_cost_pct_of_assets ? parseFloat(filters.max_admin_cost_pct_of_assets) : null,
        min_admin_expense_bps: filters.min_admin_expense_bps ? parseFloat(filters.min_admin_expense_bps) : null,
        max_admin_expense_bps: filters.max_admin_expense_bps ? parseFloat(filters.max_admin_expense_bps) : null,
        min_admin_cost_pct_of_contributions: filters.min_admin_cost_pct_of_contributions ? parseFloat(filters.min_admin_cost_pct_of_contributions) : null,
        max_admin_cost_pct_of_contributions: filters.max_admin_cost_pct_of_contributions ? parseFloat(filters.max_admin_cost_pct_of_contributions) : null,
        min_participants: filters.min_participants ? parseInt(filters.min_participants) : null,
        max_participants: filters.max_participants ? parseInt(filters.max_participants) : null,
        min_assets: filters.min_assets ? parseFloat(filters.min_assets) : null,
        max_assets: filters.max_assets ? parseFloat(filters.max_assets) : null,
        min_participation_rate: filters.min_participation_rate ? parseFloat(filters.min_participation_rate) / 100 : null,
        max_participation_rate: filters.max_participation_rate ? parseFloat(filters.max_participation_rate) / 100 : null,
        min_plan_age_years: filters.min_plan_age_years ? parseInt(filters.min_plan_age_years) : null,
        size_category: filters.size_category || null,
        states: filters.states.length > 0 ? filters.states : null,
        industry_codes: filters.industry_codes.length > 0 ? filters.industry_codes : null,
        exclude_plan_categories: filters.exclude_plan_categories.length > 0 ? filters.exclude_plan_categories : null,
        exclude_plan_subtypes: filters.exclude_plan_subtypes.length > 0 ? filters.exclude_plan_subtypes : null,
        exclude_terminated: filters.exclude_terminated,
        sort_by: filters.sort_by,
        sort_order: filters.sort_order,
        limit: filters.limit,
        offset: (page - 1) * filters.limit
      };
      
      const response = await api.searchProspects(searchFilters);
      setResults(response.results);
      setTotalResults(response.total_results);
      setSummaryStats(response.summary_stats);
    } catch (err) {
      console.error('Search error:', err);
      setError('Failed to search prospects. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setLoading(true);
    try {
      // Convert all filter values properly (matching handleSearch)
      const searchFilters = {
        min_admin_cost_per_participant: filters.min_admin_cost_per_participant ? parseFloat(filters.min_admin_cost_per_participant) : null,
        max_admin_cost_per_participant: filters.max_admin_cost_per_participant ? parseFloat(filters.max_admin_cost_per_participant) : null,
        min_admin_cost_pct_of_assets: filters.min_admin_cost_pct_of_assets ? parseFloat(filters.min_admin_cost_pct_of_assets) : null,
        max_admin_cost_pct_of_assets: filters.max_admin_cost_pct_of_assets ? parseFloat(filters.max_admin_cost_pct_of_assets) : null,
        min_admin_expense_bps: filters.min_admin_expense_bps ? parseFloat(filters.min_admin_expense_bps) : null,
        max_admin_expense_bps: filters.max_admin_expense_bps ? parseFloat(filters.max_admin_expense_bps) : null,
        min_admin_cost_pct_of_contributions: filters.min_admin_cost_pct_of_contributions ? parseFloat(filters.min_admin_cost_pct_of_contributions) : null,
        max_admin_cost_pct_of_contributions: filters.max_admin_cost_pct_of_contributions ? parseFloat(filters.max_admin_cost_pct_of_contributions) : null,
        min_participants: filters.min_participants ? parseInt(filters.min_participants) : null,
        max_participants: filters.max_participants ? parseInt(filters.max_participants) : null,
        min_assets: filters.min_assets ? parseFloat(filters.min_assets) : null,
        max_assets: filters.max_assets ? parseFloat(filters.max_assets) : null,
        min_participation_rate: filters.min_participation_rate ? parseFloat(filters.min_participation_rate) / 100 : null,
        max_participation_rate: filters.max_participation_rate ? parseFloat(filters.max_participation_rate) / 100 : null,
        min_plan_age_years: filters.min_plan_age_years ? parseInt(filters.min_plan_age_years) : null,
        size_category: filters.size_category || null,
        states: filters.states.length > 0 ? filters.states : null,
        industry_codes: filters.industry_codes.length > 0 ? filters.industry_codes : null,
        exclude_plan_categories: filters.exclude_plan_categories.length > 0 ? filters.exclude_plan_categories : null,
        exclude_plan_subtypes: filters.exclude_plan_subtypes.length > 0 ? filters.exclude_plan_subtypes : null,
        exclude_terminated: filters.exclude_terminated,
        sort_by: filters.sort_by,
        sort_order: filters.sort_order,
        limit: 100000,  // No practical limit - export all matching records
        offset: 0
      };
      
      const response = await api.searchProspects(searchFilters);
      
      // Convert to CSV
      const headers = [
        'Company Name', 'EIN', 'Plan Number', 'Plan Name', 'City', 'State',
        'Address 1', 'Address 2', 'Phone', 'Contact Name',
        'Participants', 'Assets', 'Total Admin Cost', 'Admin Cost/Participant', 'Admin Cost % of Assets',
        'Admin Expense (bps)', 'Peer Median Cost', 'Cost vs Median %', 'Peer Percentile',
        'Estimated Savings', 'Opportunity Score', 'Industry', 'Size Category', 'Plan Age'
      ];
      
      const rows = response.results.map(r => [
        `"${(r.sponsor_name || '').replace(/"/g, '""')}"`,
        r.ein,
        r.plan_number,
        `"${(r.plan_name || '').replace(/"/g, '""')}"`,
        `"${(r.city || '').replace(/"/g, '""')}"`,
        r.state,
        `"${(r.address1 || '').replace(/"/g, '""')}"`,
        `"${(r.address2 || '').replace(/"/g, '""')}"`,
        `"${(r.phone || '').replace(/"/g, '""')}"`,
        `"${(r.contact_name || '').replace(/"/g, '""')}"`,
        r.participants,
        r.assets,
        r.admin_expenses,
        r.admin_cost_per_participant,
        r.admin_cost_pct_of_assets,
        r.admin_expense_bps,
        r.peer_median_cost,
        r.cost_vs_median_pct,
        r.peer_percentile,
        r.estimated_savings,
        r.opportunity_score,
        `"${(r.industry_name || '').replace(/"/g, '""')}"`,
        r.size_category,
        r.plan_age_years
      ]);
      
      const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
      
      // Download
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `prospects_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export error:', err);
      setError('Failed to export data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleStateToggle = (state) => {
    setFilters(prev => ({
      ...prev,
      states: prev.states.includes(state)
        ? prev.states.filter(s => s !== state)
        : [...prev.states, state]
    }));
  };

  const handleIndustryToggle = (code) => {
    setFilters(prev => ({
      ...prev,
      industry_codes: prev.industry_codes.includes(code)
        ? prev.industry_codes.filter(c => c !== code)
        : [...prev.industry_codes, code]
    }));
  };

  const handlePlanCategoryToggle = (category) => {
    setFilters(prev => ({
      ...prev,
      exclude_plan_categories: prev.exclude_plan_categories.includes(category)
        ? prev.exclude_plan_categories.filter(c => c !== category)
        : [...prev.exclude_plan_categories, category]
    }));
  };

  const handlePlanSubtypeToggle = (subtype) => {
    setFilters(prev => ({
      ...prev,
      exclude_plan_subtypes: prev.exclude_plan_subtypes.includes(subtype)
        ? prev.exclude_plan_subtypes.filter(s => s !== subtype)
        : [...prev.exclude_plan_subtypes, subtype]
    }));
  };

  const clearFilters = () => {
    setFilters({
      min_admin_cost_per_participant: '',
      max_admin_cost_per_participant: '',
      min_admin_cost_pct_of_assets: '',
      max_admin_cost_pct_of_assets: '',
      min_admin_expense_bps: '',
      max_admin_expense_bps: '',
      min_admin_cost_pct_of_contributions: '',
      max_admin_cost_pct_of_contributions: '',
      min_participants: '',
      max_participants: '',
      min_assets: '',
      max_assets: '',
      size_category: '',
      min_participation_rate: '',
      max_participation_rate: '',
      states: [],
      industry_codes: [],
      exclude_plan_categories: [],
      exclude_plan_subtypes: [],
      min_plan_age_years: '',
      exclude_terminated: true,
      sort_by: 'opportunity_score',
      sort_order: 'desc',
      limit: 50,
      offset: 0
    });
    setResults([]);
    setHasSearched(false);
  };

  const formatCurrency = (value) => {
    if (!value && value !== 0) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatNumber = (value) => {
    if (!value && value !== 0) return '-';
    return new Intl.NumberFormat('en-US').format(value);
  };

  const formatPercent = (value) => {
    if (!value && value !== 0) return '-';
    return `${(value * 100).toFixed(1)}%`;
  };

  const getScoreColor = (score) => {
    if (score >= 70) return 'text-green-600 bg-green-100';
    if (score >= 50) return 'text-yellow-600 bg-yellow-100';
    if (score >= 30) return 'text-orange-600 bg-orange-100';
    return 'text-neutral-600 bg-neutral-100';
  };

  const totalPages = Math.ceil(totalResults / filters.limit);

  return (
    <div style={{ minHeight: '100vh', background: 'white' }}>
      {/* Header - Minimal & Clean */}
      <header style={{
        background: 'white',
        borderBottom: '1px solid #e5e7eb',
        padding: '1rem 0',
        position: 'sticky',
        top: 0,
        zIndex: 10
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 2.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div 
              style={{ fontSize: '1.5rem', fontWeight: 600, color: '#1e3a8a', letterSpacing: '0.02em', cursor: 'pointer' }}
              onClick={() => navigate('/')}
            >
              Love Your 401k
            </div>
            <div style={{ display: 'flex', gap: '2.5rem', alignItems: 'center' }}>
              <a href="/" style={{ color: '#6b7280', textDecoration: 'none', fontSize: '0.9375rem' }}>Search Plans</a>
              <a href="/estimator" style={{ color: '#6b7280', textDecoration: 'none', fontSize: '0.9375rem' }}>What Should I Pay for a 401(k)?</a>
              <a href="/admin/research" style={{ color: '#6b7280', textDecoration: 'none', fontSize: '0.9375rem' }}>401(k) Research</a>
              <a href="/admin/prospecting" style={{ color: '#1e3a8a', textDecoration: 'none', fontSize: '0.9375rem', fontWeight: 600 }}>Prospecting</a>
              <a href="/admin/blog-writer" style={{ color: '#6b7280', textDecoration: 'none', fontSize: '0.9375rem' }}>Blog Writer</a>
              <a href="/admin/cohort" style={{ color: '#6b7280', textDecoration: 'none', fontSize: '0.9375rem' }}>Cohort Analysis</a>
              <a href="/faq" style={{ color: '#6b7280', textDecoration: 'none', fontSize: '0.9375rem' }}>FAQ</a>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section style={{ padding: '3rem 0 2rem 0', background: 'white' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 2.5rem' }}>
          <div style={{ textAlign: 'center', maxWidth: '900px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: '2.5rem',
              fontWeight: 900,
              color: '#111827',
              marginBottom: '1rem',
              lineHeight: 1.1,
              letterSpacing: '-0.02em'
            }}>
              Find High-Value Prospects
            </h2>
            <p style={{
              fontSize: '1.125rem',
              color: '#6b7280',
              lineHeight: 1.6
            }}>
              Search millions of 401(k) plans to find companies paying too much for their retirement plan administration.
            </p>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Filters Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white border-2 border-neutral-200 rounded-2xl p-6 sticky top-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-neutral-900">Filters</h3>
                <button
                  onClick={clearFilters}
                  className="text-sm text-teal hover:text-blue-800 font-medium"
                >
                  Clear All
                </button>
              </div>

              {/* Cost Filters */}
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-neutral-700 mb-3">Cost Filters</h4>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs text-neutral-500 mb-1">Admin Cost per Participant</label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        placeholder="Min"
                        value={filters.min_admin_cost_per_participant}
                        onChange={(e) => handleFilterChange('min_admin_cost_per_participant', e.target.value)}
                        className="w-1/2 px-3 py-2 border border-neutral-200 rounded-lg text-sm"
                      />
                      <input
                        type="number"
                        placeholder="Max"
                        value={filters.max_admin_cost_per_participant}
                        onChange={(e) => handleFilterChange('max_admin_cost_per_participant', e.target.value)}
                        className="w-1/2 px-3 py-2 border border-neutral-200 rounded-lg text-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs text-neutral-500 mb-1">Admin Cost % of Assets</label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        step="0.01"
                        placeholder="Min %"
                        value={filters.min_admin_cost_pct_of_assets}
                        onChange={(e) => handleFilterChange('min_admin_cost_pct_of_assets', e.target.value)}
                        className="w-1/2 px-3 py-2 border border-neutral-200 rounded-lg text-sm"
                      />
                      <input
                        type="number"
                        step="0.01"
                        placeholder="Max %"
                        value={filters.max_admin_cost_pct_of_assets}
                        onChange={(e) => handleFilterChange('max_admin_cost_pct_of_assets', e.target.value)}
                        className="w-1/2 px-3 py-2 border border-neutral-200 rounded-lg text-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs text-neutral-500 mb-1">Admin Expense (basis points)</label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        placeholder="Min bps"
                        value={filters.min_admin_expense_bps}
                        onChange={(e) => handleFilterChange('min_admin_expense_bps', e.target.value)}
                        className="w-1/2 px-3 py-2 border border-neutral-200 rounded-lg text-sm"
                      />
                      <input
                        type="number"
                        placeholder="Max bps"
                        value={filters.max_admin_expense_bps}
                        onChange={(e) => handleFilterChange('max_admin_expense_bps', e.target.value)}
                        className="w-1/2 px-3 py-2 border border-neutral-200 rounded-lg text-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs text-neutral-500 mb-1">Admin Cost % of Contributions</label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        step="0.1"
                        placeholder="Min %"
                        value={filters.min_admin_cost_pct_of_contributions}
                        onChange={(e) => handleFilterChange('min_admin_cost_pct_of_contributions', e.target.value)}
                        className="w-1/2 px-3 py-2 border border-neutral-200 rounded-lg text-sm"
                      />
                      <input
                        type="number"
                        step="0.1"
                        placeholder="Max %"
                        value={filters.max_admin_cost_pct_of_contributions}
                        onChange={(e) => handleFilterChange('max_admin_cost_pct_of_contributions', e.target.value)}
                        className="w-1/2 px-3 py-2 border border-neutral-200 rounded-lg text-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs text-neutral-500 mb-1">Total Administration Cost</label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        placeholder="Min"
                        value={filters.min_total_admin_expenses}
                        onChange={(e) => handleFilterChange('min_total_admin_expenses', e.target.value)}
                        className="w-1/2 px-3 py-2 border border-neutral-200 rounded-lg text-sm"
                      />
                      <input
                        type="number"
                        placeholder="Max"
                        value={filters.max_total_admin_expenses}
                        onChange={(e) => handleFilterChange('max_total_admin_expenses', e.target.value)}
                        className="w-1/2 px-3 py-2 border border-neutral-200 rounded-lg text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Size Filters */}
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-neutral-700 mb-3">Plan Size</h4>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs text-neutral-500 mb-1">Size Category</label>
                    <select
                      value={filters.size_category}
                      onChange={(e) => handleFilterChange('size_category', e.target.value)}
                      className="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm"
                    >
                      {sizeCategories.map(cat => (
                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs text-neutral-500 mb-1">Participants</label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        placeholder="Min"
                        value={filters.min_participants}
                        onChange={(e) => handleFilterChange('min_participants', e.target.value)}
                        className="w-1/2 px-3 py-2 border border-neutral-200 rounded-lg text-sm"
                      />
                      <input
                        type="number"
                        placeholder="Max"
                        value={filters.max_participants}
                        onChange={(e) => handleFilterChange('max_participants', e.target.value)}
                        className="w-1/2 px-3 py-2 border border-neutral-200 rounded-lg text-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs text-neutral-500 mb-1">Total Assets ($)</label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        placeholder="Min"
                        value={filters.min_assets}
                        onChange={(e) => handleFilterChange('min_assets', e.target.value)}
                        className="w-1/2 px-3 py-2 border border-neutral-200 rounded-lg text-sm"
                      />
                      <input
                        type="number"
                        placeholder="Max"
                        value={filters.max_assets}
                        onChange={(e) => handleFilterChange('max_assets', e.target.value)}
                        className="w-1/2 px-3 py-2 border border-neutral-200 rounded-lg text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Quality Filters */}
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-neutral-700 mb-3">Plan Quality</h4>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs text-neutral-500 mb-1">Participation Rate (%)</label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        placeholder="Min %"
                        value={filters.min_participation_rate}
                        onChange={(e) => handleFilterChange('min_participation_rate', e.target.value)}
                        className="w-1/2 px-3 py-2 border border-neutral-200 rounded-lg text-sm"
                      />
                      <input
                        type="number"
                        placeholder="Max %"
                        value={filters.max_participation_rate}
                        onChange={(e) => handleFilterChange('max_participation_rate', e.target.value)}
                        className="w-1/2 px-3 py-2 border border-neutral-200 rounded-lg text-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs text-neutral-500 mb-1">Min Plan Age (years)</label>
                    <input
                      type="number"
                      placeholder="e.g., 5"
                      value={filters.min_plan_age_years}
                      onChange={(e) => handleFilterChange('min_plan_age_years', e.target.value)}
                      className="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* State Filter */}
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-neutral-700 mb-3">
                  States {filters.states.length > 0 && `(${filters.states.length} selected)`}
                </h4>
                <div className="max-h-32 overflow-y-auto border border-neutral-200 rounded-lg p-2">
                  <div className="flex flex-wrap gap-1">
                    {statesList.map(s => (
                      <button
                        key={s.state}
                        onClick={() => handleStateToggle(s.state)}
                        className={`px-2 py-1 text-xs rounded ${
                          filters.states.includes(s.state)
                            ? 'bg-blue-900 text-white'
                            : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                        }`}
                      >
                        {s.state}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Industry Filter */}
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-neutral-700 mb-3">
                  Industries {filters.industry_codes.length > 0 && `(${filters.industry_codes.length} selected)`}
                </h4>
                <select
                  multiple
                  value={filters.industry_codes}
                  onChange={(e) => {
                    const selected = Array.from(e.target.selectedOptions, option => option.value);
                    setFilters(prev => ({ ...prev, industry_codes: selected }));
                  }}
                  className="w-full h-24 px-3 py-2 border border-neutral-200 rounded-lg text-xs"
                >
                  {industries.map(ind => (
                    <option key={ind.code} value={ind.code}>
                      {ind.name} ({formatNumber(ind.total_plans)})
                    </option>
                  ))}
                </select>
                <p className="text-xs text-neutral-400 mt-1">Hold Ctrl/Cmd to select multiple</p>
              </div>

              {/* Plan Type Exclusions */}
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-neutral-700 mb-3">
                  Exclude Plan Types
                </h4>
                
                {/* Plan Categories to Exclude */}
                <div className="mb-3">
                  <label className="block text-xs text-neutral-500 mb-2">
                    Exclude by Category {filters.exclude_plan_categories.length > 0 && `(${filters.exclude_plan_categories.length} excluded)`}
                  </label>
                  <div className="max-h-32 overflow-y-auto border border-neutral-200 rounded-lg p-2">
                    <div className="space-y-1">
                      {planCategories && planCategories.length > 0 ? (
                        planCategories.map(cat => (
                          <button
                            key={cat.plan_category}
                            onClick={() => handlePlanCategoryToggle(cat.plan_category)}
                            className={`w-full px-2 py-1.5 text-xs text-left rounded transition-colors ${
                              filters.exclude_plan_categories.includes(cat.plan_category)
                                ? 'bg-red-100 text-red-700 border border-red-300'
                                : 'bg-neutral-50 text-neutral-700 hover:bg-neutral-100'
                            }`}
                          >
                            <div className="flex justify-between items-center">
                              <span className="font-medium">{cat.plan_category}</span>
                              <span className="text-neutral-500">({formatNumber(cat.plan_count)})</span>
                            </div>
                          </button>
                        ))
                      ) : (
                        <div className="text-xs text-neutral-500 text-center py-2">
                          Loading categories...
                        </div>
                      )}
                    </div>
                  </div>
                  {filters.exclude_plan_categories.length > 0 && (
                    <p className="text-xs text-red-600 mt-1">
                      Excluding {filters.exclude_plan_categories.length} categor{filters.exclude_plan_categories.length === 1 ? 'y' : 'ies'}
                    </p>
                  )}
                </div>

                {/* Plan Subtypes to Exclude */}
                <div>
                  <label className="block text-xs text-neutral-500 mb-2">
                    Exclude by Subtype {filters.exclude_plan_subtypes.length > 0 && `(${filters.exclude_plan_subtypes.length} excluded)`}
                  </label>
                  <div className="max-h-32 overflow-y-auto border border-neutral-200 rounded-lg p-2">
                    <div className="space-y-1">
                      {planSubtypes && planSubtypes.length > 0 ? (
                        planSubtypes.map(sub => (
                          <button
                            key={sub.plan_subtype}
                            onClick={() => handlePlanSubtypeToggle(sub.plan_subtype)}
                            className={`w-full px-2 py-1.5 text-xs text-left rounded transition-colors ${
                              filters.exclude_plan_subtypes.includes(sub.plan_subtype)
                                ? 'bg-red-100 text-red-700 border border-red-300'
                                : 'bg-neutral-50 text-neutral-700 hover:bg-neutral-100'
                            }`}
                          >
                            <div className="flex justify-between items-center">
                              <span className="font-medium">{sub.plan_subtype}</span>
                              <span className="text-neutral-500">({formatNumber(sub.plan_count)})</span>
                            </div>
                          </button>
                        ))
                      ) : (
                        <div className="text-xs text-neutral-500 text-center py-2">
                          Loading subtypes...
                        </div>
                      )}
                    </div>
                  </div>
                  {filters.exclude_plan_subtypes.length > 0 && (
                    <p className="text-xs text-red-600 mt-1">
                      Excluding {filters.exclude_plan_subtypes.length} subtype{filters.exclude_plan_subtypes.length === 1 ? '' : 's'}
                    </p>
                  )}
                </div>
              </div>

              {/* Sorting */}
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-neutral-700 mb-3">Sort By</h4>
                <select
                  value={filters.sort_by}
                  onChange={(e) => handleFilterChange('sort_by', e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm mb-2"
                >
                  {sortOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <select
                  value={filters.sort_order}
                  onChange={(e) => handleFilterChange('sort_order', e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm"
                >
                  <option value="desc">Descending (High to Low)</option>
                  <option value="asc">Ascending (Low to High)</option>
                </select>
              </div>

              {/* Search Button */}
              <button
                onClick={() => handleSearch(1)}
                disabled={loading}
                className="w-full py-3 bg-blue-900 text-white font-semibold rounded-xl hover:bg-blue-800 transition-colors disabled:opacity-50"
              >
                {loading ? 'Searching...' : 'Search Prospects'}
              </button>
            </div>
          </div>

          {/* Results */}
          <div className="lg:col-span-3">
            {/* Summary Stats */}
            {hasSearched && summaryStats && Object.keys(summaryStats).length > 0 && (
              <div className="bg-gradient-to-r from-blue-50 to-teal/5 rounded-2xl p-6 mb-6 border border-blue-900/20">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-neutral-900">
                    {formatNumber(totalResults)} Prospects Found
                  </h3>
                  <button
                    onClick={handleExport}
                    disabled={loading || results.length === 0}
                    className="px-4 py-2 bg-white border-2 border-blue-900 text-teal font-semibold rounded-lg hover:bg-blue-900 hover:text-white transition-colors disabled:opacity-50"
                  >
                    Export to CSV
                  </button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div>
                    <p className="text-xs text-neutral-500 mb-1">Avg Opportunity Score</p>
                    <p className="text-xl font-bold text-blue-800">{summaryStats.avg_opportunity_score}</p>
                  </div>
                  <div>
                    <p className="text-xs text-neutral-500 mb-1">Avg Admin Cost</p>
                    <p className="text-xl font-bold text-neutral-900">{formatCurrency(summaryStats.avg_admin_cost)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-neutral-500 mb-1">Total Potential Savings</p>
                    <p className="text-xl font-bold text-green-600">{formatCurrency(summaryStats.total_potential_savings)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-neutral-500 mb-1">Total Participants</p>
                    <p className="text-xl font-bold text-neutral-900">{formatNumber(summaryStats.total_participants)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-neutral-500 mb-1">Total Assets</p>
                    <p className="text-xl font-bold text-neutral-900">{formatCurrency(summaryStats.total_assets)}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg mb-6">
                <p className="text-red-800">{error}</p>
              </div>
            )}

            {/* Loading */}
            {loading && (
              <div className="flex justify-center py-16">
                <div style={{
                  width: '48px',
                  height: '48px',
                  border: '4px solid #1e3a8a',
                  borderTopColor: 'transparent',
                  borderRadius: '50%',
                  animation: 'spin 0.6s linear infinite'
                }}></div>
              </div>
            )}

            {/* Results Table */}
            {!loading && results.length > 0 && (
              <>
                <div className="bg-white border-2 border-neutral-200 rounded-2xl overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-neutral-50 border-b border-neutral-200">
                        <tr>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-neutral-600 uppercase">Company</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold text-neutral-600 uppercase">Score</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold text-neutral-600 uppercase">Participants</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold text-neutral-600 uppercase">Assets</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold text-neutral-600 uppercase">Cost/Part</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold text-neutral-600 uppercase">% of Assets</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold text-neutral-600 uppercase">vs Median</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold text-neutral-600 uppercase">Savings</th>
                          <th className="text-center px-4 py-3 text-xs font-semibold text-neutral-600 uppercase">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-neutral-100">
                        {results.map((plan, idx) => (
                          <tr key={`${plan.ein}-${plan.plan_number}-${idx}`} className="hover:bg-neutral-50">
                            <td className="px-4 py-3">
                              <div className="font-medium text-neutral-900 text-sm">{plan.sponsor_name}</div>
                              <div className="text-xs text-neutral-500">
                                {plan.city}, {plan.state} • {plan.industry_name || 'Unknown Industry'}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <span className={`px-2 py-1 rounded-full text-sm font-bold ${getScoreColor(plan.opportunity_score)}`}>
                                {plan.opportunity_score}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right text-sm">{formatNumber(plan.participants)}</td>
                            <td className="px-4 py-3 text-right text-sm">{formatCurrency(plan.assets)}</td>
                            <td className="px-4 py-3 text-right text-sm font-medium text-neutral-900">
                              {formatCurrency(plan.admin_cost_per_participant)}
                            </td>
                            <td className="px-4 py-3 text-right text-sm">
                              {plan.admin_cost_pct_of_assets ? `${plan.admin_cost_pct_of_assets.toFixed(2)}%` : '-'}
                            </td>
                            <td className="px-4 py-3 text-right">
                              {plan.cost_vs_median_pct !== null && (
                                <span className={`text-sm font-medium ${plan.cost_vs_median_pct > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                  {plan.cost_vs_median_pct > 0 ? '+' : ''}{plan.cost_vs_median_pct}%
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <span className="text-sm font-medium text-green-600">
                                {formatCurrency(plan.estimated_savings)}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <button
                                onClick={() => navigate(`/plan-analysis/${plan.ein}/${plan.plan_number}/${plan.plan_year}`)}
                                className="px-3 py-1 bg-blue-900 text-white text-xs font-medium rounded-lg hover:bg-blue-800 transition-colors"
                              >
                                View
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center items-center gap-2 mt-6">
                    <button
                      onClick={() => handleSearch(currentPage - 1)}
                      disabled={currentPage === 1 || loading}
                      className="px-4 py-2 border border-neutral-200 rounded-lg text-sm hover:bg-neutral-50 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <span className="px-4 py-2 text-sm text-neutral-600">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      onClick={() => handleSearch(currentPage + 1)}
                      disabled={currentPage >= totalPages || loading}
                      className="px-4 py-2 border border-neutral-200 rounded-lg text-sm hover:bg-neutral-50 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}

            {/* Empty State */}
            {!loading && hasSearched && results.length === 0 && (
              <div className="text-center py-16">
                <div className="w-20 h-20 mx-auto mb-6 bg-neutral-100 rounded-2xl flex items-center justify-center">
                  <svg className="w-10 h-10 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-neutral-900 mb-2">No Prospects Found</h3>
                <p className="text-neutral-600">Try adjusting your filters to find more results.</p>
              </div>
            )}

            {/* Initial State */}
            {!loading && !hasSearched && (
              <div className="text-center py-16">
                <div className="w-20 h-20 mx-auto mb-6 bg-blue-50 rounded-2xl flex items-center justify-center">
                  <svg className="w-10 h-10 text-blue-900" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-neutral-900 mb-2">Ready to Find Prospects</h3>
                <p className="text-neutral-600 mb-4">Set your filters on the left and click "Search Prospects" to find high-value opportunities.</p>
                <div className="text-sm text-neutral-500">
                  <p className="mb-2"><strong>Pro Tips:</strong></p>
                  <ul className="space-y-1">
                    <li>• Set min admin cost &gt; $200 to find overpriced plans</li>
                    <li>• Filter by min participants &gt; 50 for viable prospects</li>
                    <li>• Look for admin cost % of assets &gt; 0.5% for high-fee plans</li>
                    <li>• Sort by Opportunity Score for best leads first</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer style={{ background: '#111827', color: 'white', padding: '2rem 0', marginTop: '3rem' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 2.5rem' }}>
          <div style={{ textAlign: 'center' }}>
            <h4 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '0.5rem' }}>Love Your 401k</h4>
            <p style={{ color: '#9ca3af', fontSize: '0.875rem' }}>
              Powered by Form 5500 data • Helping employees make better retirement decisions
            </p>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default Prospecting;
